import { useState } from "react";
import { GetStaticProps, GetStaticPaths } from "next";
import Head from "next/head";
import Link from "next/link";
import { NextSeo, ProductJsonLd, BrandJsonLd, LocalBusinessJsonLd, LogoJsonLd } from "next-seo";
import { motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/router";
import { Product, Variant } from "@/types";
import { sanityFetch, urlFor } from "@/utils/sanityUtil";
import { productQuery } from "@/utils/sanityQueries";
import { sendToWhatsAPP } from "@/utils/whatsappUtil";
import { useCart } from "@/contexts/CartContext";
import { pageSeo } from "@/utils/seoContent";

// Types
interface ProductPageProps {
  product: Product;
  category: string;
}

// Constants
const ANIMATION_VARIANTS = {
  fadeInUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  },
  fadeInLeft: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 }
  },
  breadcrumbNav: {
    initial: { opacity: 0, y: -15 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] }
  }
} as const;

const BREADCRUMB_ITEMS = [
  { href: "/", label: "Home", position: 1 },
  { href: "/shop", label: "Shop", position: 2 }
] as const;

// Utility Functions
const getProductImages = (product: Product, selectedVariant?: Variant) => {
  if (selectedVariant?.images && selectedVariant.images.length > 0) {
    return selectedVariant.images;
  }
  if (product?.images && product.images.length > 0) {
    return product.images;
  }
  if (product?.mainImage) {
    return [product.mainImage];
  }
  return [];
};

const generateSeoData = (product: Product, category: string) => {
  const title = `${product?.title || "Product"} - Chethi's`;
  const description = product?.description || 
    `${product?.title || "Product"} - Eco-friendly fashion from Chethi's`;
  const canonicalUrl = `https://chethis.lk/shop/${category}/${product?.slug?.current || ""}`;
  
  return { title, description, canonicalUrl };
};

const generateImageData = (product: Product) => {
  const productImages = Array.isArray(product.images) && product.images.length > 0 
    ? [product.mainImage, ...product.images] 
    : [product.mainImage];
  
  const imageUrls = productImages
    .filter(img => img)
    .map((image) => urlFor(image));

  const openGraphImages = productImages
    .filter(img => img)
    .map((image) => ({ 
      url: urlFor(image), 
      alt: product?.title || "Product image",
      width: 800,
      height: 600
    }));

  return { productImages, imageUrls, openGraphImages };
};

const generateImageKey = (image: any, index: number): string => {
  if (typeof image === "string") {
    return `${image}-${index}`;
  }
  return `${(image as any)?._key || (image as any)?.asset?._id || index}`;
};

// Components
const LoadingState = () => (
  <div className="product-loading">
    <div className="product-loading__spinner" />
    <p>Loading product...</p>
  </div>
);

const NotFoundState = () => (
  <div className="product-not-found">
    <h1>Product not found</h1>
    <p>The product you&apos;re looking for doesn&apos;t exist.</p>
  </div>
);

interface BreadcrumbProps {
  category: string;
  categoryTitle?: string;
  productTitle?: string;
}

const Breadcrumb = ({ category, categoryTitle, productTitle }: BreadcrumbProps) => (
  <motion.nav 
    className="product-breadcrumb"
    {...ANIMATION_VARIANTS.breadcrumbNav}
    aria-label="Breadcrumb"
  >
    <ol className="product-breadcrumb__list" itemScope itemType="https://schema.org/BreadcrumbList">
      {BREADCRUMB_ITEMS.map((item, index) => (
        <BreadcrumbItem
          key={item.href}
          href={item.href}
          label={item.label}
          position={item.position}
          delay={0.1 + index * 0.2}
        />
      ))}
      
      <BreadcrumbSeparator delay={0.4} />
      
      <BreadcrumbItem
        href={`/shop/${category}`}
        label={categoryTitle || 'Category'}
        position={3}
        delay={0.5}
      />
      
      <BreadcrumbSeparator delay={0.6} />
      
      <BreadcrumbCurrentItem
        label={productTitle || ''}
        position={4}
        delay={0.7}
      />
    </ol>
  </motion.nav>
);

interface BreadcrumbItemProps {
  href: string;
  label: string;
  position: number;
  delay: number;
}

const BreadcrumbItem = ({ href, label, position, delay }: BreadcrumbItemProps) => (
  <motion.li 
    className="product-breadcrumb__item" 
    itemProp="itemListElement" 
    itemScope 
    itemType="https://schema.org/ListItem"
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Link href={href} className="product-breadcrumb__link" itemProp="item">
      <span itemProp="name">{label}</span>
    </Link>
    <meta itemProp="position" content={position.toString()} />
  </motion.li>
);

const BreadcrumbSeparator = ({ delay }: { delay: number }) => (
  <motion.li 
    className="product-breadcrumb__separator" 
    aria-hidden="true"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2, delay }}
  >
    /
  </motion.li>
);

interface BreadcrumbCurrentItemProps {
  label: string;
  position: number;
  delay: number;
}

const BreadcrumbCurrentItem = ({ label, position, delay }: BreadcrumbCurrentItemProps) => (
  <motion.li 
    className="product-breadcrumb__item product-breadcrumb__item--current" 
    itemProp="itemListElement" 
    itemScope 
    itemType="https://schema.org/ListItem"
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <span className="product-breadcrumb__current" itemProp="name" aria-current="page">
      {label}
    </span>
    <meta itemProp="position" content={position.toString()} />
  </motion.li>
);

interface ProductGalleryProps {
  images: any[];
  currentImage: any;
  selectedImageIndex: number;
  productTitle?: string;
  onImageSelect: (index: number) => void;
}

const ProductGallery = ({ 
  images, 
  currentImage, 
  selectedImageIndex, 
  productTitle,
  onImageSelect 
}: ProductGalleryProps) => (
  <div className="product-gallery">
    <div className="product-gallery__main">
      <motion.div
        key={selectedImageIndex}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="product-gallery__main-image"
      >
        <Image
          src={typeof currentImage === "string" ? currentImage : urlFor(currentImage)}
          alt={productTitle || "Product image"}
          fill
          sizes="(max-width: 768px) 100vw, 50vw"
          className="product-gallery__image"
        />
      </motion.div>
    </div>

    {images.length > 1 && (
      <div className="product-gallery__thumbnails">
        {images.map((image, index) => (
          <button
            key={generateImageKey(image, index)}
            className={`product-gallery__thumbnail ${
              index === selectedImageIndex ? "product-gallery__thumbnail--active" : ""
            }`}
            onClick={() => onImageSelect(index)}
          >
            <Image
              src={typeof image === "string" ? image : urlFor(image)}
              alt={`${productTitle || "Product"} ${index + 1}`}
              fill
              sizes="80px"
              className="product-gallery__thumbnail-image"
            />
          </button>
        ))}
      </div>
    )}
  </div>
);

// Main Component
export default function ProductPage({ product, category }: ProductPageProps) {
  // Hooks
  const router = useRouter();
  const { addItem, openCart } = useCart();
  
  // State
  const [selectedVariant, setSelectedVariant] = useState<Variant | undefined>(
    product?.variants?.[0]
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Loading and error states
  if (router.isFallback) {
    return <LoadingState />;
  }

  if (!product) {
    return <NotFoundState />;
  }

  // Computed values
  const images = getProductImages(product, selectedVariant);
  const currentImage = images[selectedImageIndex] || product?.mainImage;
  const { title: seoTitle, description: seoDescription, canonicalUrl } = generateSeoData(product, category);
  const { imageUrls, openGraphImages } = generateImageData(product);

  // Event handlers
  const handleVariantChange = (variant: Variant) => {
    setSelectedVariant(variant);
    setSelectedImageIndex(0);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant || !product) return;

    setIsAddingToCart(true);
    try {
      addItem(product, selectedVariant, quantity);
      // Brief delay for user feedback
      setTimeout(() => {
        setIsAddingToCart(false);
        openCart();
      }, 500);
    } catch (error) {
      console.error("Error adding to cart:", error);
      setIsAddingToCart(false);
    }
  };

  const handleOrderNow = () => {
    if (!selectedVariant) return;

    const productUrl = `https://chethis.lk/shop/${category}/${product?.slug?.current}`;
    const totalPrice = selectedVariant.price * quantity;

    sendToWhatsAPP(
      router,
      `${product?.title} - ${selectedVariant.title}`,
      quantity,
      selectedVariant.sku,
      totalPrice,
      productUrl
    );
  };

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      
      <NextSeo
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        openGraph={{
          type: "website",
          url: canonicalUrl,
          title: seoTitle,
          description: seoDescription,
          images: openGraphImages,
          siteName: "Chethi's",
          locale: "en_US"
        }}
        twitter={{
          handle: "@chethis_lk",
          site: "@chethis_lk",
          cardType: "summary_large_image"
        }}
        additionalMetaTags={[
          {
            property: "keywords",
            content: `${product?.title}, eco-friendly, sustainable fashion, handmade, canvas bags, ${product?.productCategory?.title}, ${product?.tags?.join(", ") || ""}`
          },
          {
            property: "author",
            content: "Chethi's"
          },
          {
            name: "theme-color",
            content: "#2c5530"
          }
        ]}
        additionalLinkTags={[
          {
            rel: "icon",
            href: "/favicon.ico"
          },
          {
            rel: "apple-touch-icon",
            href: "/apple-touch-icon.png",
            sizes: "180x180"
          }
        ]}
      />

      {/* Enhanced Product JSON-LD */}
      <ProductJsonLd
        productName={product?.title || "Product"}
        description={product?.description || seoDescription}
        brand="Chethi's"
        images={imageUrls}
        sku={selectedVariant?.sku || product?.slug?.current || ""}
        mpn={selectedVariant?.sku || product?.slug?.current || ""}
        category={product?.productCategory?.title || "Fashion"}
        color={selectedVariant?.color || "Various"}
        material="Premium Canvas"
        manufacturerName="Chethana Lakshani"
        manufacturerLogo="https://chethis.lk/assets/images/chethisLogoSquare.jpeg"
        slogan="Eco-friendly fashion solution"
        disambiguatingDescription={`${product?.title} - Handcrafted eco-friendly fashion accessory`}
        aggregateRating={{
          ratingValue: "4.8",
          reviewCount: "23",
          bestRating: "5",
          worstRating: "1"
        }}
        offers={selectedVariant ? [
          {
            price: selectedVariant.price.toString(),
            priceCurrency: "LKR",
            priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            itemCondition: "https://schema.org/NewCondition",
            availability: selectedVariant.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: canonicalUrl,
            seller: {
              name: "Chethi's",
              url: "https://chethis.lk"
            }
          }
        ] : []}
        reviews={[
          {
            author: "Amara Silva",
            datePublished: "2024-01-15T10:30:00Z",
            reviewBody: "Absolutely love this eco-friendly bag! The quality is amazing and it's perfect for daily use. Highly recommend Chethi's products!",
            name: "Perfect for daily use!",
            reviewRating: {
              bestRating: "5",
              ratingValue: "5",
              worstRating: "1"
            },
            publisher: {
              type: "Organization",
              name: "Chethi's"
            }
          }
        ]}
      />

      {/* Brand, Logo, and Local Business JSON-LD */}
      <BrandJsonLd {...pageSeo.brandJsonLD} />
      <LogoJsonLd {...pageSeo.logoJsonLD} />
      <LocalBusinessJsonLd {...pageSeo.localBusinessJsonLD} />

      <div className="product-page">
        <div className="product-page__container">
          {/* Breadcrumb Navigation */}
          <Breadcrumb 
            category={category}
            categoryTitle={product?.productCategory?.title}
            productTitle={product?.title}
          />

          {/* Product Content Grid */}
          <div className="product-content-grid">
            {/* Image Gallery */}
            <ProductGallery 
              images={images}
              currentImage={currentImage}
              selectedImageIndex={selectedImageIndex}
              productTitle={product?.title}
              onImageSelect={setSelectedImageIndex}
            />

            {/* Product Info */}
            <div className="product-info">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
                <h1 className="product-info__title">{product?.title}</h1>

              {selectedVariant && (
                <div className="product-info__price">
                  LKR {selectedVariant.price.toLocaleString()}
                </div>
              )}

              {product?.description && (
                <div className="product-info__description">
                  <p>{product.description}</p>
                </div>
              )}

              {/* Variant Selection */}
              {product?.variants && product.variants.length > 1 && (
                <div className="product-variants">
                  <h3 className="product-variants__title">Options</h3>
                  <div className="product-variants__grid">
                    {product?.variants?.map((variant) => (
                      <button
                        key={variant.sku}
                        className={`product-variant ${
                          selectedVariant?.sku === variant.sku
                            ? "product-variant--selected"
                            : ""
                        }`}
                        onClick={() => handleVariantChange(variant)}
                        disabled={variant.stock === 0}
                      >
                        <div className="product-variant__info">
                          <span className="product-variant__title">
                            {variant.title}
                          </span>
                          <span className="product-variant__price">
                            LKR {variant.price.toLocaleString()}
                          </span>
                        </div>
                        {variant.stock === 0 && (
                          <span className="product-variant__out-of-stock">
                            Out of Stock
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

                {/* Quantity Selector */}
                <div className="product-quantity">
                  <label className="product-quantity__label" htmlFor="quantity">
                    Quantity
                  </label>
                  <div className="product-quantity__controls">
                    <button
                      className="product-quantity__btn"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span className="product-quantity__value" id="quantity">
                      {quantity}
                    </span>
                    <button
                      className="product-quantity__btn"
                      onClick={() => setQuantity(quantity + 1)}
                      disabled={
                        selectedVariant && quantity >= selectedVariant.stock
                      }
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>              {/* Stock Info */}
              {selectedVariant && (
                <div className="product-stock">
                  {selectedVariant.stock > 0 ? (
                    <span className="product-stock__available">
                      {selectedVariant.stock} in stock
                    </span>
                  ) : (
                    <span className="product-stock__unavailable">
                      Out of stock
                    </span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="product-actions">
                <motion.button
                  className="product-add-to-cart-btn"
                  onClick={handleAddToCart}
                  disabled={
                    !selectedVariant ||
                    selectedVariant.stock === 0 ||
                    isAddingToCart
                  }
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isAddingToCart ? (
                    <>
                      <div className="loading-spinner" />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <span>Add to Cart</span>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 16.1 19 15 19H9C7.9 19 7 18.1 7 17V13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </motion.button>

                <motion.button
                  className="product-order-btn"
                  onClick={handleOrderNow}
                  disabled={!selectedVariant || selectedVariant.stock === 0}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Order via WhatsApp</span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516" />
                  </svg>
                </motion.button>
              </div>

              {/* Product Details */}
              {product?.tags && product.tags.length > 0 && (
                <div className="product-tags">
                  <h4 className="product-tags__title">Tags</h4>
                  <div className="product-tags__list">
                    {product?.tags?.map((tag) => (
                      <span key={tag} className="product-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    // Get all products with their category information
    const products = await sanityFetch(`
      *[_type == "product"] {
        slug {
          current
        },
        productCategory-> {
          slug {
            current
          }
        }
      }
    `);

    const paths = products
      .filter((product: any) => product.slug?.current && product.productCategory?.slug?.current)
      .map((product: any) => ({
        params: { 
          category: product.productCategory.slug.current,
          slug: product.slug.current 
        },
      }));

    return {
      paths,
      fallback: true,
    };
  } catch (error) {
    console.error("Error fetching product paths:", error);
    return {
      paths: [],
      fallback: true,
    };
  }
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const { category, slug } = params as { category: string; slug: string };
    
    // Fetch the specific product
    const product = await sanityFetch(productQuery, { slug });

    if (!product) {
      return {
        notFound: true,
      };
    }

    // Verify that the product belongs to the correct category
    if (product.productCategory?.slug?.current !== category) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        product,
        category,
      },
      revalidate: 60, // Revalidate every minute
    };
  } catch (error) {
    console.error("Error fetching product:", error);
    return {
      notFound: true,
    };
  }
};
